import {
  faEnvelope,
  faPhone,
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import { TableColumn } from '@vadiun/react-components';
import { useResponsiveBreakpoint } from '@vadiun/react-hooks-legacy';
import PassengersItem from 'app/viaje/components/shared/passengers';
import Points from 'app/viaje/viajeTraslado/coordinar/components/list/points';
import ServiceType from 'app/viaje/viajeTraslado/coordinar/components/list/serviceType';
import moment from 'moment';
import { FC } from 'react';
import { Table } from 'shared/components';
import {
  ParticularTravel,
  ParticularTravelStatus,
  StatusPayment
} from '../../models/particularTravelsModel';
import ParticularClient from '../ParticularClient';
import {
  PaymentStatus,
  TypePaymentStatus
} from 'app/viaje/viajeTraslado/viajeTraslado/models/TransferReservationPartial';
import PaymentAction from './actions/PaymentAction';
import BillingAction from './actions/BillingAction';
import WhatsappAction from '../../../../../../shared/components/Table/actions-buttons/WhatsappAction';
import GmailAction from '../../../../../../shared/components/Table/actions-buttons/GmailAction';
import EyeToDetail from 'shared/components/Table/actions-buttons/EyeToDetail';
import LltEmailAction from '../../../../../../shared/components/Table/actions-buttons/LltEmailAction';
import { ParticularColumnVisibility } from '../../utils/particularStorage';
interface ParticularTravelsTableProps {
  particularTravels: {
    data: ParticularTravel[];
    total: number;
    isLoading: boolean;
    error: any;
    serverSideOptions: any;
  };
  onChangePage: (newPage: number) => void;
  onChangeRowsPerPage: (newRowsPerPage: number) => void;
  onSortChange: (direction: string | null, sortBy: string) => void;
  handlePageChange: (currentPage: number) => void;
  filters: any;
  onFiltersChange: (filters: any) => void;
  onManualPayment: (id: number, amount: number) => void;
  selectedClientLabel: string;
  onManualBilling: () => void;
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
  columnVisibility: ParticularColumnVisibility;
}
const ParticularTravelsTable: FC<ParticularTravelsTableProps> = ({
  particularTravels,
  onChangePage,
  onChangeRowsPerPage,
  onSortChange,
  handlePageChange,
  filters,
  onFiltersChange,
  onManualPayment,
  selectedClientLabel,
  onManualBilling,
  selectedIds,
  setSelectedIds,
  columnVisibility
}) => {
  const display = useResponsiveBreakpoint();
  const isMobileOrTablet =
    display === 'xs' || display === 'sm' || display === 'md';
  const checkServiceType = (val: string) => (val === 'VIP' ? 1 : 0);
  const isFilterStateFacturado =
    filters.state === ParticularTravelStatus.FACTURADO;
  const isFilterStatePaid =
    filters.status_payment !== StatusPayment.UNPAID &&
    filters.status_payment !== StatusPayment.PENDING;

  const data = (particularTravels.data ?? [])
    .map((reservation) => ({
      ...reservation,
      passengers: reservation.locations
        .map((location) => {
          if (location.passengers && location.passengers.length > 0) {
            return location.passengers?.map((passenger) => passenger.name);
          }
          return null;
        })
        .flat(),
      amount: reservation.payment_info.amount
    }))
    .sort((a: any, b: any) => {
      const aDate = moment(a.trip_end_time).format('DD-MM HH:mm');
      const bDate = moment(b.trip_end_time).format('DD-MM HH:mm');

      if (aDate === bDate) {
        return (
          b.hasPriority - a.hasPriority ||
          checkServiceType(b.serviceType) - checkServiceType(a.serviceType)
        );
      } else {
        return 0;
      }
    });

  const ids = data
    .filter((item) => {
      if (isFilterStateFacturado) {
        return item.has_required_bill && !item.has_send_bill;
      } else {
        return item.payment_info.states.some(
          (state) =>
            state.status_name === TypePaymentStatus.APPROVED ||
            state.status_name === TypePaymentStatus.PAID
        );
      }
    })
    .map((item) => item.id);

  const isAllSelected =
    ids.length > 0 && ids.every((id) => selectedIds.includes(id));
  const toggleAll = () => {
    setSelectedIds(
      isAllSelected
        ? selectedIds.filter((id) => !ids.includes(id))
        : Array.from(new Set([...selectedIds, ...ids]))
    );
  };

  const toggleOne = (id: number) => {
    setSelectedIds(
      selectedIds.includes(id)
        ? selectedIds.filter((i) => i !== id)
        : [...selectedIds, id]
    );
  };

  const getCurrentState = (states: PaymentStatus[]) => {
    return states.length > 0
      ? [...states].sort(
          (a, b) => b.datetime.valueOf() - a.datetime.valueOf()
        )[0]
      : null;
  };

  const columns: TableColumn<any>[] = [
    {
      name: 'select',
      label: '',
      options: {
        filter: false,
        sort: false,
        display: isFilterStatePaid && ids.length > 0 ? true : false,
        customHeadRender: () => (
          <div
            className="flex items-center justify-center "
            style={{ position: 'relative' }}
          >
            <div
              className="flex items-center justify-center"
              style={{
                position: 'absolute',
                top: -4,
                width: '100%',
                borderBottom: '1px solid #e0e0e0',
                paddingBottom: '10px'
              }}
            >
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleAll}
                aria-label="Seleccionar todos"
                style={{
                  width: '20px',
                  height: '20px'
                }}
              />
            </div>
          </div>
        ),
        customBodyRenderLite: (index) => {
          const id = data[index].id;
          const hasPayment = data[index].payment_info.states.some(
            (state) =>
              state.status_name === TypePaymentStatus.APPROVED ||
              state.status_name === TypePaymentStatus.PAID
          );
          const hasBilling =
            data[index].status === ParticularTravelStatus.FACTURADO;
          const hasRequiredBilling = data[index].has_required_bill;
          const hasSendBilling = data[index].has_send_bill;
          const checked = selectedIds.includes(id);
          return (
            <div className="flex justify-center items-center">
              {((!hasBilling && hasPayment) ||
                (hasBilling && hasRequiredBilling && !hasSendBilling)) && (
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(id)}
                  aria-label={`Seleccionar ${id}`}
                  style={{
                    width: '16px',
                    height: '16px'
                  }}
                />
              )}
            </div>
          );
        }
      }
    },
    {
      name: 'id',
      label: isMobileOrTablet ? ' ' : 'ID',
      options: {
        display: columnVisibility.id ? 'true' : 'excluded',
        filter: false,
        customBodyRender: (value) => {
          return (
            <div className="flex justify-between text-sm">
              {isMobileOrTablet ? <p>ID</p> : null}
              <p className="text-xs">{value}</p>
            </div>
          );
        }
      }
    },
    {
      name: 'cliente',
      label: isMobileOrTablet ? ' ' : 'Cliente',
      options: {
        display: columnVisibility.cliente ? 'true' : 'excluded',
        filter: false,
        customBodyRender: (value, { rowIndex: index }) => {
          const current = data[index];
          return (
            <div className="flex items-center gap-4">
              <ParticularClient
                isMobileOrTablet={isMobileOrTablet}
                client={{
                  name: current.client.name,
                  companyName: current.client.company_name,
                  type: current.type,
                  service: current.service
                }}
              />
              <div className="flex justify-between items-center gap-3">
                <div className="flex flex-col justify-between items-center gap-3">
                  {current.client.personalInfo?.whatsappUrl && (
                    <WhatsappAction
                      waUrl={current.client.personalInfo?.whatsappUrl ?? ''}
                    />
                  )}
                  {current.client.personalInfo?.phone && (
                    <FontAwesomeIcon
                      icon={faPhone}
                      style={{
                        width: '14px',
                        height: '14px',
                        cursor: 'pointer'
                      }}
                      className="text-gray-500 hover:text-gray-700"
                      title="Copiar teléfono"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          current.client.personalInfo?.phone ?? ''
                        );
                        alert('Teléfono copiado al portapapeles');
                      }}
                    />
                  )}
                </div>
                {current.client.personalInfo?.email && (
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex flex-col justify-between items-center gap-3">
                      <GmailAction
                        emailAddress={current.client.personalInfo?.email}
                      />
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        style={{
                          width: '14px',
                          height: '14px',
                          cursor: 'pointer'
                        }}
                        className="text-gray-500 hover:text-gray-700"
                        title="Copiar email"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            current.client.personalInfo?.email ?? ''
                          );
                          alert('Email copiado al portapapeles');
                        }}
                      />
                    </div>
                    <LltEmailAction
                      emailAddress={current.client.personalInfo?.email}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        }
      }
    },
    {
      name: 'trip_end_time',
      label: isMobileOrTablet ? ' ' : 'Finalizado a las',
      options: {
        display: columnVisibility.trip_end_time ? 'true' : 'excluded',
        filter: false,
        customBodyRender: (tripEndTime, { rowIndex: index }) => {
          const current = data[index];
          return (
            <div className="flex  text-sm">
              {isMobileOrTablet ? <p>Finalizado a las</p> : null}
              <div className="flex w-32 justify-center items-center gap-2">
                <span
                  className=" title text-blue-800 "
                  title="Salida de chofer"
                >
                  {current.trip_end_time.format('DD/MM')}
                </span>
                <span
                  className=" text-xs text-blue-500"
                  title="Salida de chofer"
                >
                  {current.trip_end_time.format('HH:mm')}
                  <QueryBuilderIcon
                    fontSize="small"
                    className="text-blue-500 pb-1"
                  />
                </span>
              </div>
            </div>
          );
        }
      }
    },
    {
      name: 'amount',
      label: isMobileOrTablet ? ' ' : 'Monto',
      options: {
        display: columnVisibility.amount ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: (value, { rowIndex: index }) => {
          const current = data[index];
          const currentStatus = getCurrentState(
            current?.payment_info?.states ?? []
          );
          if (
            currentStatus?.status_name === TypePaymentStatus.PAID ||
            currentStatus?.status_name === TypePaymentStatus.APPROVED
          ) {
            return (
              <p style={{ color: '#000000' }}>{value ? `$ ${value}` : '-'}</p>
            );
          }
          return (
            <p style={{ color: '#FF0000' }}>{value ? `$ ${value}` : '-'}</p>
          );
        }
      }
    },
    {
      name: 'status_payment',
      label: isMobileOrTablet ? ' ' : 'Estado de pago',
      options: {
        display: columnVisibility.status_payment ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: (value, { rowIndex: index }) => {
          const current = data[index];

          const currentStatus = getCurrentState(
            current?.payment_info?.states ?? []
          );

          return (
            <div className="flex justify-center items-center w-32">
              {isMobileOrTablet ? <p>Estado de pago</p> : null}
              <p
                style={{
                  color:
                    currentStatus?.status_name !== TypePaymentStatus.PAID &&
                    currentStatus?.status_name !== TypePaymentStatus.APPROVED
                      ? '#FF0000'
                      : '#000000'
                }}
              >
                {currentStatus?.status_name === TypePaymentStatus.PAID
                  ? 'Pagado Manual'
                  : currentStatus?.status_name === TypePaymentStatus.APPROVED
                  ? 'Pagado'
                  : 'Impago'}
              </p>
            </div>
          );
        }
      }
    },
    {
      name: 'puntos',
      label: isMobileOrTablet ? ' ' : 'Domicilios',
      options: {
        display: columnVisibility.puntos ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: (value, { rowIndex: index }) => {
          return (
            <Points
              data={data}
              index={index}
              isMobileOrTablet={isMobileOrTablet}
              onLocationsDetails={() => {}}
              showInfo={false}
            />
          );
        }
      }
    },
    {
      name: 'coordinationObservation',
      label: isMobileOrTablet ? ' ' : 'Obs. Coordinación',
      options: {
        display: columnVisibility.coordinationObservation ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          return (
            <div className="flex justify-between text-sm w-32">
              {isMobileOrTablet ? <p>Obs. Coordinación</p> : null}
              <p className="text-xs">{value}</p>
            </div>
          );
        }
      }
    },
    {
      name: 'general_observation',
      label: isMobileOrTablet ? ' ' : 'Obs. Chofer',
      options: {
        display: columnVisibility.general_observation ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          return (
            <div className="flex justify-between text-sm w-32">
              {isMobileOrTablet ? <p>Obs. Chofer</p> : null}
              <p className="text-xs">{value || ''}</p>
            </div>
          );
        }
      }
    },
    {
      name: 'service',
      label: isMobileOrTablet ? ' ' : 'Servicio',
      options: {
        display: columnVisibility.service ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: (value, { rowIndex: index }) => (
          <ServiceType
            data={data}
            index={index}
            isMobileOrTablet={isMobileOrTablet}
          />
        )
      }
    },
    {
      name: 'assigned_driver',
      label: 'Chofer',
      options: {
        display: columnVisibility.assigned_driver ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customBodyRenderLite: (index) => (
          <div className="center flex  flex-col space-y-1 ">
            <p className="text-sm font-medium text-blue-600">
              {data[index].assigned_driver?.codigo}
            </p>
            <p className=" text-xs text-gray-700">
              {data[index].assigned_driver?.nombreCompleto}
            </p>
          </div>
        )
      }
    },
    {
      name: 'passengers',
      label: isMobileOrTablet ? ' ' : 'Pasajeros',
      options: {
        display: columnVisibility.passengers ? 'true' : 'excluded',
        filter: false,
        download: false,
        sort: false,
        customBodyRender: (value, { rowIndex: index }) => {
          const passengers = data[index]?.passengers;
          return (
            <div className="flex justify-between text-sm">
              {isMobileOrTablet ? <p>Pasajeros</p> : null}
              {passengers && (
                <PassengersItem
                  passengers={passengers}
                  idTravel={data[index].id}
                />
              )}
            </div>
          );
        }
      }
    },
    {
      name: 'status',
      label: isMobileOrTablet ? ' ' : 'Facturado',
      options: {
        display: columnVisibility.status ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: (value, { rowIndex: index }) => {
          const isBilling = value === ParticularTravelStatus.FACTURADO;
          return (
            <div className="flex justify-start items-center w-32 pl-8">
              {isMobileOrTablet ? <p>Estado</p> : null}
              {isBilling ? (
                <div
                  style={{
                    color: '#FF9C00',
                    borderRadius: '50%',
                    boxShadow: '0px 0px 8px #FF9C00',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
              ) : (
                <div
                  style={{
                    color: '#8A8A8A',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faTimesCircle} />
                </div>
              )}
            </div>
          );
        }
      }
    },
    {
      name: 'has_required_bill',
      label: isMobileOrTablet ? ' ' : 'Factura requerida',
      options: {
        display: columnVisibility.has_required_bill ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customBodyRender: (value, { rowIndex: index }) => {
          const has_send_bill = data[index].has_send_bill;
          return (
            <div className="flex justify-center items-center w-32 gap-4">
              {isMobileOrTablet ? <p>Factura requerida</p> : null}
              {value ? (
                <div
                  style={{
                    color: '#008282',
                    borderRadius: '50%',
                    boxShadow: '0px 0px 8px #008282',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
              ) : (
                <div
                  style={{
                    color: '#8A8A8A',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faTimesCircle} />
                </div>
              )}
              {value && has_send_bill && (
                <div
                  style={{
                    color: '#008282',
                    fontSize: '13px',
                    padding: '2px',
                    borderRadius: '4px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #008282'
                  }}
                >
                  Enviado
                </div>
              )}
              {value && !has_send_bill && (
                <div
                  style={{
                    color: '#008282',
                    fontSize: '13px',
                    padding: '2px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  Pendiente
                </div>
              )}
            </div>
          );
        }
      }
    },
    {
      label: isMobileOrTablet ? ' ' : 'Acciones',
      name: 'actions',
      options: {
        display: columnVisibility.actions ? 'true' : 'excluded',
        filter: false,
        sort: false,
        customHeadRender: () => (
          <div
            className="flex items-center justify-center"
            style={{
              height: '40px',
              position: 'relative',
              minWidth: '80px'
            }}
          >
            <div
              className="flex flex-col items-center gap-2 w-full"
              style={{
                position: 'absolute',
                top: -1.5,
                left: 0,
                right: 0
              }}
            >
              <span
                className="text-sm w-full text-center "
                style={{
                  borderBottom: '1px solid #e0e0e0',
                  paddingBottom: '5px'
                }}
              >
                Acciones
              </span>
              <div
                className="flex w-full justify-center gap-x-2"
                style={{
                  borderBottom: '1px solid #e0e0e0',
                  paddingBottom: '6px'
                }}
              >
                <BillingAction
                  onManualBilling={onManualBilling}
                  isDisabled={
                    isFilterStateFacturado ||
                    !isFilterStatePaid ||
                    !selectedIds ||
                    selectedIds.length < 1
                  }
                />
              </div>
            </div>
          </div>
        ),
        customBodyRender: (value, { rowIndex: index }) => {
          const id = data[index].id;
          const isBilling =
            data[index].status === ParticularTravelStatus.FACTURADO;

          const hasPayment = data[index].payment_info.states.some(
            (state) =>
              state.status_name === TypePaymentStatus.APPROVED ||
              state.status_name === TypePaymentStatus.PAID
          );
          return (
            <div key={id} className="flex gap-4 justify-center items-center">
              {isMobileOrTablet ? <p>Acciones</p> : null}
              <EyeToDetail route={'/main/operations/trip-details/' + id} />
              {!hasPayment && (
                <PaymentAction
                  onManualPayment={onManualPayment}
                  id={id}
                  amount={data[index].amount ?? 0}
                />
              )}
            </div>
          );
        }
      }
    }
  ];

  return (
    <div className="w-full">
      <Table
        options={{
          filter: false,
          search: false,
          download: false,
          elevation: 0,
          selectableRows: 'none',
          print: false,
          serverSide: true,
          viewColumns: false,
          onChangePage(page: number) {
            onChangePage(page + 1);
            handlePageChange(page + 1);
          },
          onChangeRowsPerPage(rowsPerPage: number) {
            onChangeRowsPerPage(rowsPerPage);
          },
          onColumnSortChange: (
            changedColumn: string,
            direction: 'asc' | 'desc'
          ) => {
            onSortChange(direction, changedColumn);
          },
          ...particularTravels.serverSideOptions
        }}
        title={''}
        data={data}
        columns={columns}
      />
    </div>
  );
};

export default ParticularTravelsTable;
